import { forwardRef, ReactNode, TextareaHTMLAttributes, useId } from 'react';
import { cn } from '../../lib/cn';

/**
 * Textarea — control multilinea con label, helper y error opcionales.
 *
 * Comparte la misma anatomia/visual que `Input`, sin iconos leading/trailing
 * (no aplican). Soporta `resize` controlado via prop.
 */
export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  helperText?: ReactNode;
  error?: ReactNode;
  invalid?: boolean;
  /** Tipo de resize permitido (default vertical). */
  resize?: 'none' | 'vertical' | 'auto';
}

const resizeClasses: Record<NonNullable<TextareaProps['resize']>, string> = {
  none: 'resize-none',
  vertical: 'resize-y',
  auto: 'resize-y', // 'auto' se simula con altura dinamica via JS; aqui usamos resize-y como fallback.
};

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      id,
      label,
      helperText,
      error,
      invalid,
      required,
      disabled,
      className,
      resize = 'vertical',
      rows = 4,
      ...props
    },
    ref,
  ) => {
    const autoId = useId();
    const textareaId = id ?? autoId;
    const helperId = `${textareaId}-helper`;
    const errorId = `${textareaId}-error`;
    const isInvalid = Boolean(error) || invalid === true;

    const describedBy =
      [error ? errorId : null, helperText ? helperId : null].filter(Boolean).join(' ') || undefined;

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={textareaId}
            className="block text-caption font-body font-semibold text-primary-900 mb-2"
          >
            {label}
            {required && (
              <span aria-hidden="true" className="ml-1 text-error">
                *
              </span>
            )}
            {required && <span className="sr-only"> (requerido)</span>}
          </label>
        )}

        <div
          className={cn(
            'w-full bg-cream-50 rounded',
            'border transition-colors duration-2 ease-out',
            isInvalid ? 'border-error' : 'border-cream-200 hover:border-primary-500',
            'focus-within:border-primary-700 focus-within:shadow-elev1',
            disabled && 'opacity-50 cursor-not-allowed',
          )}
        >
          <textarea
            ref={ref}
            id={textareaId}
            rows={rows}
            required={required}
            disabled={disabled}
            aria-invalid={isInvalid || undefined}
            aria-describedby={describedBy}
            className={cn(
              'block w-full bg-transparent text-body text-primary-900 placeholder:text-mute-500',
              'px-3 py-2 outline-none',
              'disabled:cursor-not-allowed',
              resizeClasses[resize],
              className,
            )}
            {...props}
          />
        </div>

        {error ? (
          <p id={errorId} role="alert" className="mt-2 text-caption text-error">
            {error}
          </p>
        ) : helperText ? (
          <p id={helperId} className="mt-2 text-caption text-primary-900/70">
            {helperText}
          </p>
        ) : null}
      </div>
    );
  },
);

Textarea.displayName = 'Textarea';
